import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'onefaceui-react-components';
import { useDispatch, useSelector } from 'react-redux';
import { Tabs, Tab, TabList, TabPanel } from 'react-tabs';
import { push } from 'connected-react-router';

import Modal from 'react/components/Modal';
import queryClient from 'react/services/queryClient';
import defaultFetch from 'react/api/sagas/fetch/defaultFetch';
import { useEventDetails } from 'react/queries/eventDetails';
import { actionCreators as actionsModals } from 'react/api/actions/modals';
import { actionCreators as actionsChat } from 'react/api/actions/chat';
import { queryKey } from 'react/queries/EventLog';
import { checkPermissions, getResourceTo } from 'react/utils/utils';

import { CommentsPanel } from './Panels/CommentsPanel';
import { EventInfoPanel } from './Panels/EventInfoPanel';
import { Container, ModalFooter, TabWrapper } from './styles';

export default function EventDetailsModal() {
  const dispatch = useDispatch();

  const MODAL_KEY = 'eventDetails';
  const {
    title,
    isOpen,
    data: modalData
  } = useSelector((state) => state.modals.get(MODAL_KEY).toJS());
  const taskTabExists = useSelector((state) =>
    state.tabs.hasIn(['list', 'Task'])
  );

  const { eventLogId } = modalData || {};

  const [userId, setUserId] = useState(null);

  const { data, isFetching, error, refetch } = useEventDetails({
    eventLogId,
    currentOwner: { id: userId }
  });

  const [dataEvent, setDataEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const driver = useMemo(() => {
    if (dataEvent?.route?.driverAssignments?.length) {
      return dataEvent?.route?.driverAssignments[0].driver;
    }

    return dataEvent?.driver;
  }, [dataEvent]);

  const closeModal = useCallback(() => {
    dispatch(
      actionsModals.closeModal({
        modalKey: MODAL_KEY
      })
    );
    setUserId(null);
    setDataEvent({});
  }, []);

  const closeEvent = async () => {
    setIsLoading(true);
    const response = await defaultFetch(
      '/EventLogNotes',
      {
        closeException: true,
        eventLog: {
          id: eventLogId
        }
      },
      'POST'
    );

    if (response.error) {
      const monetMessage = new Monet.Form.Messages.InlineMessage();
      monetMessage.setScope('#eventDetailsModalWrapper');
      monetMessage.setInfoType('error');
      monetMessage.setMessage(getResourceTo('eventDetails.internal.error'));
      monetMessage.triggerMessage('error');
    }

    setDataEvent((prev) => ({ ...prev, closed: response.closeException }));
    setIsLoading(false);
    queryClient.invalidateQueries(queryKey);
    App.Forms?.EventLog?.filterBuilder?.executeFilter();
  };

  const openChat = useCallback(() => {
    const payload = {
      from: driver.id,
      fromName: driver.name
    };

    dispatch(actionsChat.addNewChatWindow(payload));
  }, [driver]);

  const goToNewTask = () => {
    Monet.Session.Task.eventLog = dataEvent;
    const taskFormIsOpen = window.location.href.match(/Task\/new/g);

    if (!taskFormIsOpen) {
      dispatch(push('/Task/new'));
    }

    if (taskTabExists) {
      App.Objects.Task.form_new();
    }

    closeModal();
  };

  const getContainer = () => {
    let container = document.querySelector(
      '.mainContainer .reactivo-tabs.gmTabs #eventDetailsModalWrapper'
    );

    if (!container) {
      container = document.createElement('div');
      container.id = 'eventDetailsModalWrapper';

      document
        .querySelector('.mainContainer .reactivo-tabs.gmTabs')
        .appendChild(container);
    }

    return container;
  };

  const getMessageEventLogLocked = ({
    key,
    parameters: { currentOwner, currentOpenedWhen }
  }) => {
    const translatedMessage = getResourceTo(key);
    if (currentOwner) {
      const currentOwnerKey = currentOwner.match(/key=(.+)/)[1].split(/,|]/)[0];
      const openedWhen = Monet.Format.DateTime.formatToLocale(
        `${currentOpenedWhen}+0000`
      );

      return translatedMessage
        .replace('{currentOpenedWhen}', openedWhen)
        .replace('{currentOwner}', currentOwnerKey);
    }
    return translatedMessage;
  };

  useEffect(() => {
    const [event] = data || [];

    if (isOpen) setDataEvent(event);
  }, [data, isOpen]);

  useEffect(() => {
    if (eventLogId) refetch();
  }, [eventLogId, userId]);

  useEffect(() => {
    if (error?.status === 409) {
      setDataEvent({});

      const errorMessage = error.error.errorMessages[0];

      const confirmAlert = () => {
        const currentOwnerId = errorMessage.resource.parameters.currentOwner
          .match(/id=(.+)/)[1]
          .split(/,|]/)[0];

        setUserId(currentOwnerId);
      };

      new Greenmile.Components.AlertModal(
        getMessageEventLogLocked(errorMessage.resource),
        null,
        confirmAlert,
        closeModal
      ).show();
    }
  }, [error]);

  const ModalFooterContainer = (
    <ModalFooter justifyContent="flex-end" gap={12}>
      <Button
        dataTestId="close-modal-button"
        variant="secondary"
        onClick={closeModal}
      >
        {getResourceTo('default.button.cancel')}
      </Button>
      <Button
        dataTestId="close-event-button"
        disabled={!!dataEvent?.closed}
        variant="secondary"
        loading={isLoading}
        onClick={closeEvent}
      >
        {getResourceTo('notificationpanel.btnshowcloseevent')}
      </Button>
      {checkPermissions('EVENTLOG_OPEN_CHAT') && (
        <Button
          dataTestId="open-chat-button"
          variant="primary"
          type="button"
          onClick={() => {
            if (driver) openChat();
          }}
          disabled={!driver}
        >
          {getResourceTo('eventDetails.modal.btn.openChat')}
        </Button>
      )}
      {checkPermissions('TASK_CREATE') && (
        <Button
          dataTestId="open-new-task-button"
          variant="primary"
          type="button"
          onClick={goToNewTask}
        >
          {getResourceTo('EventLog.AddNewTask')}
        </Button>
      )}
    </ModalFooter>
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closeModal]);

  return (
    <Modal
      className={`eventDetails event-${dataEvent?.id}`}
      isOpen={isOpen}
      title={getResourceTo(title)}
      crossAxisBtn={false}
      closeModalFn={closeModal}
      parentSelector={() => getContainer()}
      footer={ModalFooterContainer}
      shouldCloseOnOverlayClick
      onRequestClose={closeModal}
    >
      <Container data-testid="eventDetails-container">
        <div className="monetInlineAlerContainer" />
        <TabWrapper>
          <Tabs defaultIndex={0}>
            <TabList>
              <Tab>{getResourceTo('eventDetails.modal.tab.eventInfo')}</Tab>
              <Tab>{getResourceTo('eventDetails.modal.tab.comments')}</Tab>
            </TabList>
            <TabPanel>
              <EventInfoPanel dataEvent={dataEvent} isFetching={isFetching} />
            </TabPanel>
            <TabPanel>
              {eventLogId && (
                <CommentsPanel
                  closed={!!dataEvent?.closed}
                  eventLogId={eventLogId}
                />
              )}
            </TabPanel>
          </Tabs>
        </TabWrapper>
      </Container>
    </Modal>
  );
}
